import { IPaystackReference } from "../Types";

declare global {
  interface Window {
    PaystackPop?: {
      setup: (options: Record<string, unknown>) => { openIframe: () => void };
    };
  }
}

let paystackScriptPromise: Promise<void> | null = null;

function waitForPaystackPop(timeoutMs = 10000): Promise<void> {
  if (window.PaystackPop) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const startedAt = Date.now();

    const check = () => {
      if (window.PaystackPop) {
        resolve();
        return;
      }

      if (Date.now() - startedAt >= timeoutMs) {
        reject(new Error("Paystack failed to initialize"));
        return;
      }

      window.setTimeout(check, 50);
    };

    check();
  });
}

export function loadPaystackScript(): Promise<void> {
  if (window.PaystackPop) {
    return Promise.resolve();
  }

  if (paystackScriptPromise) {
    return paystackScriptPromise;
  }

  paystackScriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector(
      'script[src="https://js.paystack.co/v1/inline.js"]'
    );

    if (existingScript) {
      waitForPaystackPop()
        .then(resolve)
        .catch((error) => {
          paystackScriptPromise = null;
          reject(error);
        });
      return;
    }

    const script = document.createElement("script");
    script.src = "https://js.paystack.co/v1/inline.js";
    script.async = true;
    script.onload = () => {
      waitForPaystackPop()
        .then(resolve)
        .catch((error) => {
          paystackScriptPromise = null;
          reject(error);
        });
    };
    script.onerror = () => {
      paystackScriptPromise = null;
      reject(new Error("Failed to load Paystack"));
    };
    document.body.appendChild(script);
  });

  return paystackScriptPromise;
}

export function isPaystackReady(): boolean {
  return Boolean(window.PaystackPop);
}

export interface PaystackPaymentConfig {
  publicKey: string;
  email: string;
  amount: number;
  reference: string;
  currency?: string;
}

export function openPaystackPayment(
  config: PaystackPaymentConfig,
  onSuccess: (reference: IPaystackReference) => void,
  onClose: () => void
) {
  if (!window.PaystackPop) {
    throw new Error("Paystack is not available");
  }

  const handler = window.PaystackPop.setup({
    key: config.publicKey,
    email: config.email,
    amount: config.amount,
    ref: config.reference,
    currency: config.currency || "NGN",
    callback: (response: IPaystackReference) => {
      onSuccess(response);
    },
    onClose: () => {
      onClose();
    },
  });

  handler.openIframe();
}
